import { SpeakerFaceCoordinatesResponse } from '@goldcast/api/content';
import tinycolor from 'tinycolor2';
import { BACKGROUND_COLOR_DEFAULT, TEXT_COLOR_DEFAULT, OUTLINE_COLOR_DEFAULT } from '../constants/colors';
import { FONT_DEFAULT_VALUE } from '../constants/fonts';
import { DEFAULT_FONT_SIZE } from '../constants/player';
import { CaptionLineConfig, CaptionStyle } from '../types/asset-types';
import type { Clip } from '../types/clip-types';
import { getCaptionStyles } from './captionUtils';
import { isClipAudioContent, isSplitsInTimelineEnabledPure, getAllCompositeSegments, getClipFPS } from './clipUtils';
import { rgbaToHex } from './colorUtils';
import { getDefaultSceneData } from './sceneUtils';
import { migrateSpeakerLabelStyles } from './speakerLabelUtils';
import { ContentMetadataStoreFactoryType } from '../stores/contentMetadataStoreFactory';

export function updateClipDefaultProperties({
  clip,
  facialRecognitionToDefaultPosition,
  isSplitsTranslationEnabled,
  isFullFaceRecEnabled,
  isFullFaceRecForPreRecordedEnabled,
  contentMetadataStoreInstance
}: {
  clip: Clip;
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
  isSplitsTranslationEnabled: boolean;
  isFullFaceRecEnabled: boolean;
  isFullFaceRecForPreRecordedEnabled: boolean;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
}) {
  if (!clip.asset_metadata?.layout) {
    clip.asset_metadata = {
      ...(clip.asset_metadata || {}),
      layout: 'DEFAULT',
      size: 'LANDSCAPE'
    };
  }

  if (clip.asset_metadata && !clip.asset_metadata.magicLayout) {
    clip.asset_metadata.magicLayout = {
      backgroundColor: rgbaToHex(clip.content?.project?.theme_obj?.theme_primary_color!) || BACKGROUND_COLOR_DEFAULT,
      showSpeakerLabels: true,
      textColor: rgbaToHex(clip.content?.project?.theme_obj.primary_text_color!) || TEXT_COLOR_DEFAULT
    };
  } else if (clip.asset_metadata?.magicLayout) {
    clip.asset_metadata.magicLayout = {
      ...clip.asset_metadata.magicLayout,
      backgroundColor: clip.asset_metadata.magicLayout.backgroundColor || BACKGROUND_COLOR_DEFAULT,
      textColor: clip.asset_metadata.magicLayout.textColor || TEXT_COLOR_DEFAULT
    };
  }

  if (clip.asset_metadata.hide_borders === undefined) {
    clip.asset_metadata.hide_borders = !clip.asset_metadata.subtitle;
  }

  if (isClipAudioContent(clip)) {
    clip.asset_metadata.layout = 'AUDIOGRAM';
  }

  if (!clip.asset_metadata.fit) {
    clip.asset_metadata.fit = 'FIT';
  }

  const isSplitsEnabled = isSplitsInTimelineEnabledPure({ clip, isSplitsTranslationEnabled });
  if (clip.asset_metadata.player_config && !isSplitsEnabled && (clip.asset_metadata.scenes || []).length === 0) {
    const newScenes = getDefaultSceneData({
      clip: clip,
      facialRecognitionToDefaultPosition,
      isFullFaceRecEnabled,
      isFullFaceRecForPreRecordedEnabled,
      contentMetadataStoreInstance,
      isSplitsTranslationEnabled
    });
    clip.asset_metadata.scenes = [newScenes];
    const allCompositeSegments = getAllCompositeSegments(clip);
    allCompositeSegments.forEach(compositeSegment => {
      if (compositeSegment.segment_metadata) {
        compositeSegment.segment_metadata.scenes = [newScenes];
      }
    });
  }

  if (clip.asset_metadata.scenes) {
    const sortedScenes = clip.asset_metadata.scenes.sort((a, b) => a.fromFrame - b.fromFrame);
    const firstScene = sortedScenes[0];
    const lastScene = sortedScenes[sortedScenes.length - 1];
    const fps = getClipFPS(clip);
    if (
      firstScene &&
      firstScene.fromFrame !== clip.asset_metadata.start * fps &&
      firstScene.toFrame > clip.asset_metadata.start * fps
    ) {
      firstScene.fromFrame = clip.asset_metadata.start * fps;
    }
    if (
      lastScene &&
      lastScene.toFrame !== clip.asset_metadata.end * fps &&
      lastScene.fromFrame < clip.asset_metadata.end * fps
    ) {
      lastScene.toFrame = clip.asset_metadata.end * fps;
    }
  }

  if (Array.isArray(clip.asset_metadata?.captions)) {
    clip.asset_metadata.captions = clip.asset_metadata.captions.map((caption: any, index: number) => ({
      ...caption,
      index
    }));
  } else {
    clip.asset_metadata.captions = [];
  }

  // START - CR-4289 - captions-undo-fix
  // This code is same as in sharedAPI.ts. That is required for clip handling. This is required for history handling
  // Temp code. Will be cleaned after removal of shared api
  const showCaptions = !!clip.asset_metadata.subtitle;
  if (
    clip.asset_metadata.caption_positions === undefined &&
    clip.asset_metadata.caption_styles?.placement === undefined &&
    showCaptions &&
    clip.asset_metadata.layout !== 'AUDIOGRAM'
  ) {
    clip.asset_metadata = {
      ...clip.asset_metadata,
      caption_positions: {
        x: '2.5%',
        y: '82%',
        width: '95%',
        height: '18%'
      } as any
    };
  } else if (
    clip.asset_metadata.caption_positions &&
    typeof clip.asset_metadata.caption_positions?.height !== 'string' &&
    clip.asset_metadata.layout !== 'AUDIOGRAM' &&
    showCaptions
  ) {
    clip.asset_metadata = {
      ...clip.asset_metadata,
      caption_positions: {
        ...clip.asset_metadata.caption_positions,
        height: parseFloat(clip.asset_metadata.caption_positions.height as unknown as string) + '%',
        x: parseFloat(clip.asset_metadata.caption_positions.x as unknown as string) + '%',
        y: parseFloat(clip.asset_metadata.caption_positions.y as unknown as string) + '%',
        width: parseFloat(clip.asset_metadata.caption_positions.width as unknown as string) + '%'
      } as any
    };
  }
  // END - CR-4289 - captions-undo-fix
  if (!clip.asset_metadata.maxVideoDuration && clip.asset_metadata.is_full_length) {
    if (clip.asset_metadata.deletes) {
      const totalDeletes = Object.values(clip.asset_metadata.deletes).reduce(
        (acc, curr) => acc + (curr.bounds[1] - curr.bounds[0]),
        0
      );
      clip.asset_metadata.maxVideoDuration = (clip.asset_metadata.duration || 0) + totalDeletes;
    } else {
      clip.asset_metadata.maxVideoDuration = clip.asset_metadata.end;
    }
  }

  clip.asset_metadata.speaker_label_styles = migrateSpeakerLabelStyles(clip.asset_metadata.speaker_label_styles);

  // Migration of caption styles to new schema as per https://goldcast.atlassian.net/browse/CR-6425
  if (!clip.asset_metadata.caption_styles?.theme_preset) {
    clip.asset_metadata.caption_styles = {
      caps: clip.asset_metadata.caption_styles?.caps || false,
      font_size: clip.asset_metadata.caption_styles?.font_size || DEFAULT_FONT_SIZE,
      placement: clip.asset_metadata.caption_styles?.placement,
      line_config: clip.asset_metadata.caption_styles?.line_config || CaptionLineConfig.OneLine,
      font_location:
        clip.asset_metadata.caption_styles?.font_location ||
        clip.asset_metadata.font_location ||
        FONT_DEFAULT_VALUE.url,
      theme_preset: 'custom',
      pageStyle: {
        background: clip.asset_metadata.caption_styles?.background_style_color
          ? tinycolor(clip.asset_metadata.caption_styles.background_style_color).setAlpha(0.6).toHex8String()
          : undefined,
        paddingLeft: '16px',
        paddingRight: '16px',
        borderRadius: '8px',
        color: clip.asset_metadata.magicLayout?.textColor || TEXT_COLOR_DEFAULT
      },
      wordStyle: {
        active: {
          textShadow: getCaptionStyles(CaptionStyle.Outline, 1, undefined, OUTLINE_COLOR_DEFAULT).textShadow,
          color: clip.asset_metadata.highlight_type === 'text' ? clip.asset_metadata.text_highlight_color : undefined
        },
        inactive: {
          textShadow: getCaptionStyles(CaptionStyle.Outline, 1, undefined, OUTLINE_COLOR_DEFAULT).textShadow,
          color: TEXT_COLOR_DEFAULT
        }
      },
      letterStyle: undefined,

      // Deprecated fields
      scale: clip.asset_metadata.caption_styles?.scale || 1,
      animation: clip.asset_metadata.caption_styles?.animation || CaptionStyle.Outline,
      background_style_color: clip.asset_metadata.caption_styles?.background_style_color
    };
  }
}
